import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MailCheck } from 'lucide-react';
import { useState, type JSX } from 'react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { InvitationResultDialog } from '@/components/InvitationResultDialog';
import { usePlatformPermissions } from '@/features/auth/hooks/usePlatformPermissions';

import styles from './TenantLifecyclePanel.module.scss';
import {
  getTenantAdminInvitation,
  resendTenantAdminInvitation,
  revokeTenantAdminInvitation,
  type TenantAdminInvitationResult,
  type TenantAdminInvitationStatus,
} from '../api/tenants.graphql';
import badgeStyles from '../pages/TenantsPage.module.scss';
import {
  ADMIN_INVITATION_PANEL_ID,
  formatLifecycleDate,
  STATUS_REASON_LIMITS,
} from '../tenant-labels';

const STATUS_VIEW: Record<TenantAdminInvitationStatus, { label: string; tone: string }> = {
  ACTIVATED: { label: 'Cuenta activada', tone: 'active' },
  PENDING: { label: 'Invitación pendiente', tone: 'pilot' },
  EXPIRED: { label: 'Invitación vencida o revocada', tone: 'past_due' },
  NOT_SENT: { label: 'Sin invitación', tone: 'cancelled' },
  UNKNOWN: { label: 'Sin administrador inicial registrado', tone: 'cancelled' },
};

/**
 * OS-1719 — administrador inicial del tenant: estado de su invitación por email y acciones para
 * reenviarla o revocarla (auditadas en el API). Nunca muestra contraseñas: el link de respaldo
 * aparece una sola vez, solo si el email del reenvío no se confirmó.
 */
export function TenantAdminInvitationPanel({ tenantId }: { tenantId: string }): JSX.Element {
  const qc = useQueryClient();
  const { can } = usePlatformPermissions();
  const canManage = can('platform.tenants.create');
  const [result, setResult] = useState<TenantAdminInvitationResult | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const queryKey = ['platform-tenant-admin-invitation', tenantId];
  const state = useQuery({ queryKey, queryFn: () => getTenantAdminInvitation(tenantId) });

  const resend = useMutation({
    mutationFn: () => resendTenantAdminInvitation(tenantId),
    onSuccess: (sent) => {
      setResult(sent);
      void qc.invalidateQueries({ queryKey });
    },
  });
  const revoke = useMutation({
    mutationFn: (reason: string) => revokeTenantAdminInvitation(tenantId, reason),
    onSuccess: (next) => {
      qc.setQueryData(queryKey, next);
      setRevoking(false);
      setNotice('Invitación revocada: el link ya no sirve.');
    },
  });

  const data = state.data;
  const view = data ? STATUS_VIEW[data.status] : null;
  const canResend =
    canManage && data !== undefined && ['PENDING', 'EXPIRED', 'NOT_SENT'].includes(data.status);
  const canRevoke = canManage && data?.status === 'PENDING';

  return (
    <section
      id={ADMIN_INVITATION_PANEL_ID}
      tabIndex={-1}
      className={styles.panel}
      aria-labelledby="tenant-admin-invitation-title"
    >
      <div className={styles.head}>
        <h2 id="tenant-admin-invitation-title" className={styles.title}>
          Administrador inicial
          {view ? (
            <span className={`${badgeStyles.badge} ${badgeStyles[view.tone] ?? ''}`}>{view.label}</span>
          ) : null}
        </h2>
        <div className={styles.actions}>
          {canResend ? (
            <button
              type="button"
              className={`${styles.actionBtn} ${styles.primary}`}
              disabled={resend.isPending}
              onClick={() => {
                setNotice(null);
                resend.mutate();
              }}
            >
              <MailCheck size={14} aria-hidden="true" />
              {resend.isPending ? 'Enviando…' : 'Reenviar invitación'}
            </button>
          ) : null}
          {canRevoke ? (
            <button
              type="button"
              className={`${styles.actionBtn} ${styles.danger}`}
              onClick={() => {
                setNotice(null);
                setRevoking(true);
              }}
            >
              Revocar invitación
            </button>
          ) : null}
        </div>
      </div>

      {state.isLoading ? <p className={styles.reason} role="status">Cargando…</p> : null}
      {state.isError ? (
        <p className={styles.reason} role="alert">
          No se pudo cargar la invitación: {state.error.message}
        </p>
      ) : null}
      {data && data.adminEmail ? (
        <ul className={styles.timeline} aria-label="Invitación del administrador">
          <li>
            <span className={styles.term}>Email</span>
            <span className={styles.value}>{data.adminEmail}</span>
          </li>
          {data.sentAt ? (
            <li>
              <span className={styles.term}>Último envío</span>
              <span className={styles.value}>{formatLifecycleDate(data.sentAt)}</span>
            </li>
          ) : null}
          {data.expiresAt && data.status === 'PENDING' ? (
            <li>
              <span className={styles.term}>Vence</span>
              <span className={styles.value}>{formatLifecycleDate(data.expiresAt)}</span>
            </li>
          ) : null}
        </ul>
      ) : null}
      {data?.status === 'UNKNOWN' ? (
        <p className={styles.reason}>
          Este tenant se creó antes de la invitación por email: sus administradores entran con su
          contraseña o con "Olvidé mi contraseña".
        </p>
      ) : null}
      {resend.error ? (
        <p className={styles.reason} role="alert">
          {resend.error.message}
        </p>
      ) : null}
      <p className={styles.notice} role="status">
        {notice}
      </p>

      {revoking ? (
        <ConfirmDialog
          title="Revocar invitación"
          confirmLabel="Revocar"
          cancelLabel="Volver"
          danger
          pending={revoke.isPending}
          error={revoke.error?.message ?? null}
          reason={{
            label: 'Motivo (queda en la auditoría)',
            placeholder: 'Ej.: el correo del administrador estaba mal escrito',
            ...STATUS_REASON_LIMITS,
          }}
          onConfirm={(reason) => revoke.mutate(reason)}
          onCancel={() => setRevoking(false)}
        >
          <p>
            El link enviado deja de servir. La cuenta sigue sin contraseña hasta que reenvíes una
            invitación.
          </p>
        </ConfirmDialog>
      ) : null}
      {result && data?.adminEmail ? (
        <InvitationResultDialog
          email={data.adminEmail}
          delivery={result.emailDelivery}
          link={result.activationUrl}
          expiresAt={result.expiresAt}
          resendAction="Reenviar invitación"
          purpose="definir su contraseña y activar la cuenta"
          onClose={() => setResult(null)}
        />
      ) : null}
    </section>
  );
}
